import { Checkbox, CheckboxSize, Show } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "shared/components/Popover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  UserCircle02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { Card } from "shared/components/Card";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import { leadApi } from "api/lead";

type AssigneeFilterPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: number[] | null;
  onSelectedUserIdsChange: (userIds: number[] | null) => void;
  onPageChange: (page: string) => void;
};

export const AssigneeFilterPopover = ({
  open,
  onOpenChange,
  selectedUserIds,
  onSelectedUserIdsChange,
  onPageChange
}: AssigneeFilterPopoverProps) => {
  const { auth } = useAuthContext();
  const { data: assignees, isLoading } = useQuery({
    queryKey: ["LEAD_ASSIGNEES"],
    queryFn: () => leadApi.getAssignees(),
    enabled: open
  });

  const handleToggleUser = (userId: number) => {
    const isSelected = selectedUserIds?.includes(userId) || false;
    if (isSelected) {
      const newIds = selectedUserIds?.filter((id) => id !== userId) || [];
      onSelectedUserIdsChange(newIds.length > 0 ? newIds : null);
    } else onSelectedUserIdsChange([...(selectedUserIds || []), userId]);
    onPageChange("1");
  };

  const isAnySelected = selectedUserIds === null;
  const selectedCount = selectedUserIds?.length || 0;

  const sortedUsers = useMemo(() => {
    const users = assignees || [];
    const sorted = users.toSorted((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    if (auth) {
      const withoutAuth = sorted.filter((u) => u.id !== auth.id);
      return [auth, ...withoutAuth];
    }
    return sorted;
  }, [assignees, auth]);

  const selectedUsersData = useMemo(() => {
    if (!selectedUserIds || selectedUserIds.length === 0) return [];
    return selectedUserIds
      .map((id) => {
        const isCurrentUser = id === auth?.id;
        const user = sortedUsers.find((u) => u.id === id);
        const name = user?.name || (isCurrentUser ? auth.name : null);
        if (!name) return null;
        return { id, name, isCurrentUser };
      })
      .filter(Boolean) as {
      id: number;
      name: string;
      isCurrentUser: boolean;
    }[];
  }, [selectedUserIds, sortedUsers, auth]);

  const displayText = useMemo(() => {
    if (isAnySelected) return "Any";
    if (selectedCount > 0)
      return selectedUsersData.map((u) => u.name).join(", ");
    return "Any";
  }, [isAnySelected, selectedCount, selectedUsersData]);

  const visibleSelectedUsers = selectedUsersData.slice(0, 3);
  const extraSelectedCount = Math.max(
    selectedCount - visibleSelectedUsers.length,
    0
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="bottom-start">
      <PopoverTrigger onClick={() => onOpenChange(true)}>
        <Card className="w-full px-4 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-2 min-h-[40px] h-full shadow-xs">
          <HugeiconsIcon
            icon={UserCircle02Icon}
            className="text-content-200"
            size={20}
          />
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-content-200 font-bold text-xs shrink-0">
              Assigned to:{" "}
            </span>
            {selectedCount > 0 ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex items-center -space-x-2 shrink-0">
                  {visibleSelectedUsers.map((user) => (
                    <Avatar
                      key={user.id}
                      name={user.name}
                      className="text-[9px] text-content-1000 border-2 border-content-1000-inverted"
                      size={20}
                    />
                  ))}
                  {extraSelectedCount > 0 ? (
                    <div className="size-5 rounded-full bg-alpha-60 text-content-1000 text-[9px] font-bold flex items-center justify-center border-2 border-content-1000-inverted">
                      +{extraSelectedCount}
                    </div>
                  ) : null}
                </div>
                <span className="font-bold text-content-1000 text-xs truncate">
                  {displayText}
                </span>
              </div>
            ) : (
              <span className="font-bold text-content-1000 text-xs truncate">
                {displayText}
              </span>
            )}
          </div>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className={cx(
              "text-content-1000 shrink-0",
              open ? "rotate-180" : ""
            )}
          />
        </Card>
      </PopoverTrigger>
      <PopoverContent className="max-w-[314px] w-full p-0 rounded-lg border border-alpha-100 shadow-lg bg-content-1000-inverted z-10">
        <div className="flex w-full items-center justify-between gap-3 px-4 py-3 border-b border-alpha-40">
          <span className="text-sm font-semibold text-content-1000">
            Assigned to
          </span>
          <button
            type="button"
            onClick={() => {
              onSelectedUserIdsChange(null);
              onPageChange("1");
              onOpenChange(false);
            }}
            className="text-sm font-medium text-primary-500 focus-visible:outline-none focus-visible:ring-0"
          >
            Cancel
          </button>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          <Show if={isLoading}>
            <div className="px-4 py-2.5 text-content-400 text-sm">
              Loading users...
            </div>
          </Show>
          <Show if={!isLoading && sortedUsers.length === 0}>
            <div className="px-4 py-2.5 text-content-400 text-sm">
              No users found
            </div>
          </Show>
          {sortedUsers.map((user) => {
            const isSelected = selectedUserIds?.includes(user.id) || false;
            const isCurrentUser = user.id === auth?.id;
            return (
              <div
                key={user.id}
                className="flex w-full items-center justify-start gap-3 px-4 py-2.5 hover:bg-alpha-40 cursor-pointer"
                onClick={() => handleToggleUser(user.id)}
              >
                <Checkbox
                  checked={isSelected}
                  size={CheckboxSize.SM}
                  wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                  onChange={() => {}}
                />
                <Avatar
                  name={user.name}
                  className="text-[9px] text-content-1000"
                  size={22}
                />
                <p className="text-base font-medium text-content-1000">
                  {user.name}
                  {isCurrentUser && (
                    <span className="text-content-200">&nbsp;(You)</span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
